import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

/* ═══════════════════════════════════════════════════════
   AXIANS CASE STUDY — AI-Powered VFX
   ═══════════════════════════════════════════════════════ */

const VIDEOS = {
  before: 'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/scene6bh.mp4?alt=media&token=bee7c601-acda-4e50-b1a8-40d2bbb022f7',
  after: 'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/With%20Foreground.mp4?alt=media&token=b927ab55-0e98-495d-be57-0e6890a8d435',
};

const PIPELINE = [
  {
    step: '01',
    title: 'Footage Preparation',
    desc: 'Exported green screen footage from Premiere Pro as MP4 — dramatically reducing file size from the original ProRes format. Smaller files are essential for AI model inputs that have strict size and format constraints.',
    tools: ['Premiere Pro', 'H.264 Export'],
  },
  {
    step: '02',
    title: 'Start Frame Generation',
    desc: "Extracted the first frame of the original footage and used AI image models to replace the green screen with a packed football stadium. This generated start frame was based on artist-created sketches of the desired stadium layout — jerseys, crowd density, goal area positioning.",
    tools: ['NanoBanana Pro', 'ChatGPT Image-2', 'Artist Sketches'],
  },
  {
    step: '03',
    title: 'End Frame + Keyframe Generation',
    desc: "Because the camera fully changes perspective, we generated a matching end frame showing the client's specific POV requirements — the goal area with celebrating players and dejected opponents. A mid-point keyframe was also created to guide the AI through the camera transition, eliminating guesswork.",
    tools: ['AI Image Generation', '3 Keyframes Total'],
  },
  {
    step: '04',
    title: 'AI Video Synthesis',
    desc: 'Fed the three keyframes (start, middle, end) into Seedance\u00A02.0 along with the original green screen footage with masked foreground areas. The model synthesized the stadium background with a moving, cheering crowd — maintaining spatial consistency across the full camera pan.',
    tools: ['Seedance 2.0', 'Beeble Switch X', 'Foreground Masking'],
  },
  {
    step: '05',
    title: 'Frame Upscaling & Reformatting',
    desc: 'The AI output was at 30\u00A0FPS — but the client required 25\u00A0FPS ProRes\u00A04444 in 2K Rec.709. We used our custom ComfyUI + RealESRGAN pipeline to upscale every frame individually, then recomposed at the target framerate. Frame duplication artifacts from the 30→25 conversion were resolved through interpolation and PAL speed adjustment.',
    tools: ['ComfyUI', 'RealESRGAN', 'Frame Interpolation', 'PAL Speed'],
  },
  {
    step: '06',
    title: 'Final Delivery',
    desc: 'Composited the upscaled AI background with the original foreground elements, color-graded to match the Rec.709 specification, and exported as ProRes\u00A04444 at 25\u00A0FPS — broadcast-ready for the client\'s delivery pipeline.',
    tools: ['ProRes 4444', '25 FPS', '2K · Rec.709'],
  },
];

const CALENDLY = 'https://calendly.com/jagan-motionx/30min';

/* ── Animation variants ──────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};

/* ── Reusable section wrapper ────────────────── */
function Section({ children, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  return (
    <motion.section
      ref={ref}
      variants={stagger}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      className={`max-w-[1200px] mx-auto px-6 md:px-10 lg:px-10 py-20 md:py-28 ${className}`}
    >
      {children}
    </motion.section>
  );
}

export default function StadiumVfxCaseStudy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Case Study: Stadium VFX — AI-Powered Green Screen Replacement | MotionX';
  }, []);

  return (
    <div className="bg-[#050505] text-white min-h-screen font-['Inter'] relative">
      {/* Ambient background glows */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#E50914]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute top-[40%] left-0 w-[400px] h-[400px] bg-[#E50914]/[0.02] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-[30%] w-[500px] h-[500px] bg-blue-500/[0.01] rounded-full blur-[150px]" />
      </div>

      {/* ── Nav ─────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 md:px-10 py-5 flex justify-between items-center bg-[#050505]/60 backdrop-blur-xl border-b border-white/[0.06]">
        <Link to="/" className="flex items-center gap-2.5 no-underline">
          <div className="w-7 h-7 bg-[#E50914] flex items-center justify-center font-['Oswald'] text-[10px] font-bold tracking-wider text-white">MX</div>
          <span className="font-['Oswald'] text-sm font-semibold tracking-[0.15em] uppercase text-white">MotionX</span>
        </Link>
        <Link to="/" className="text-[11px] text-white/45 tracking-[0.1em] uppercase no-underline hover:text-white transition-colors"><span className="hidden sm:inline">← Back to </span>Studio</Link>
      </nav>

      {/* ── Hero — Prime Video style ────────────── */}
      <header className="relative min-h-screen overflow-hidden">
        {/* Background video — right-aligned, fading from black */}
        <div className="absolute inset-0">
          <video
            autoPlay muted loop playsInline preload="auto"
            src={VIDEOS.after}
            className="absolute right-0 top-0 h-full w-full md:w-[70%] object-cover"
          />
          {/* Gradient: black on left, fades to transparent on right */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505] via-[20%] md:via-[45%] to-[#050505]/60 md:to-transparent" />
          {/* Bottom fade */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
          {/* Film grain */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence baseFrequency=\'0.9\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col justify-center pt-24 pb-20 px-6 md:px-10 lg:px-16 max-w-[1200px] mx-auto">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-[#E50914] text-[10px] tracking-[0.3em] uppercase mb-4">
            Case Study
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-['Oswald'] text-4xl md:text-6xl lg:text-7xl font-bold uppercase leading-[0.95] tracking-tight max-w-[600px]">
            Stadium VFX: <span className="text-white/45">AI-Powered Green Screen for Live-Action</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[15px] font-extralight text-white/45 max-w-[480px] mt-6 leading-relaxed">
            How we replaced green screen footage with a dynamic, cheering football stadium
            using Seedance&nbsp;2.0, Beeble, and ComfyUI — delivering broadcast-ready VFX
            in ProRes 4444 at a fraction of traditional cost.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-2 md:flex gap-6 md:gap-12 mt-12">
            {[
              ['VFX', 'Service Type'],
              ['25 FPS', 'ProRes 4444 · 2K'],
              ['Rec.709', 'Color Space'],
              ['100%', 'Client-Owned IP'],
            ].map(([value, label]) => (
              <div key={label}>
                <div className="font-['Oswald'] text-2xl md:text-3xl font-bold">{value}</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/20 mt-1">{label}</div>
              </div>
            ))}
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          >
            <span className="text-[9px] tracking-[0.2em] uppercase text-white/15 font-['Inter']">Scroll</span>
            <div className="w-px h-8 overflow-hidden">
              <motion.div
                className="w-px h-8 bg-white/20"
                animate={{ y: ['-100%', '100%'] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </motion.div>
        </div>
      </header>

      <div className="h-px bg-white/[0.06] max-w-[1200px] mx-auto" />

      {/* ── Before / After Videos ───────────────── */}
      <Section>
        <motion.p variants={fadeUp} className="text-[#E50914] text-[10px] tracking-[0.3em] uppercase mb-3">The Transformation</motion.p>
        <motion.h2 variants={fadeUp} className="font-['Oswald'] text-3xl md:text-5xl uppercase tracking-tight">Before & After</motion.h2>
        <motion.p variants={fadeUp} className="text-sm font-extralight text-white/45 max-w-[640px] mt-4 leading-relaxed">
          Raw green screen footage shot on set — transformed into a packed football stadium
          with a cheering crowd, celebrating players, and full environmental depth.
        </motion.p>
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <div className="relative rounded-xl overflow-hidden border border-white/[0.06]">
            <span className="absolute top-4 left-4 z-10 bg-black/60 backdrop-blur-md px-3.5 py-1.5 rounded-md font-['Oswald'] text-[10px] tracking-[0.2em] uppercase border border-white/[0.08]">Raw Footage</span>
            <video autoPlay muted loop playsInline preload="metadata" src={VIDEOS.before} className="w-full aspect-video object-cover block" />
          </div>
          <div className="relative rounded-xl overflow-hidden border border-white/[0.06]">
            <span className="absolute top-4 left-4 z-10 bg-[#E50914]/30 backdrop-blur-md px-3.5 py-1.5 rounded-md font-['Oswald'] text-[10px] tracking-[0.2em] uppercase border border-[#E50914]/20">AI-Transformed</span>
            <video autoPlay muted loop playsInline preload="metadata" src={VIDEOS.after} className="w-full aspect-video object-cover block" />
          </div>
        </motion.div>
      </Section>

      <div className="h-px bg-white/[0.06] max-w-[1200px] mx-auto" />

      {/* ── The Challenge ──────────────────────── */}
      <Section>
        <motion.p variants={fadeUp} className="text-[#E50914] text-[10px] tracking-[0.3em] uppercase mb-3">The Challenge</motion.p>
        <motion.h2 variants={fadeUp} className="font-['Oswald'] text-3xl md:text-5xl uppercase tracking-tight">Why This Shot<br/>Was Different</motion.h2>
        <motion.p variants={fadeUp} className="text-sm font-extralight text-white/45 max-w-[640px] mt-4 leading-relaxed">
          This wasn't a simple static green screen replacement. The camera moves continuously
          from the actors' faces to their POV — a full perspective shift that breaks most AI video models.
        </motion.p>
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          {[
            ['01', 'Static Crowd Problem', "Beeble's Switch\u00A0X model generated a stadium background — but the crowd was completely frozen. No movement, no cheering, no energy. For a football stadium celebrating a goal, a static crowd looks immediately wrong."],
            ['02', 'Client-Specific POV', "The camera pans from the actors taking a selfie to their phone screen — revealing the stadium from their POV. The client needed specific elements: the goal area, a celebrating team, and dejected opponents walking away. AI couldn't guess this."],
          ].map(([num, title, desc]) => (
            <div key={num} className="bg-white/[0.03] border border-white/[0.06] rounded-xl backdrop-blur-xl p-8 hover:border-white/[0.1] hover:bg-white/[0.04] transition-all duration-400">
              <div className="font-['Oswald'] text-5xl font-bold text-[#E50914]/10 leading-none mb-3">{num}</div>
              <div className="font-['Oswald'] text-base uppercase tracking-wide mb-3">{title}</div>
              <div className="text-[13px] font-extralight text-white/45 leading-relaxed">{desc}</div>
            </div>
          ))}
        </motion.div>
      </Section>

      <div className="h-px bg-white/[0.06] max-w-[1200px] mx-auto" />

      {/* ── Input / Output Frames ──────────────── */}
      <Section>
        <motion.p variants={fadeUp} className="text-[#E50914] text-[10px] tracking-[0.3em] uppercase mb-3">The Problem Visualized</motion.p>
        <motion.h2 variants={fadeUp} className="font-['Oswald'] text-3xl md:text-5xl uppercase tracking-tight">One Shot,<br/>Two Perspectives</motion.h2>
        <motion.p variants={fadeUp} className="text-sm font-extralight text-white/45 max-w-[640px] mt-4 leading-relaxed">
          The camera shifts from a front-facing selfie angle to a close-up POV of the phone screen.
          AI models need to maintain a consistent 3D stadium environment across this entire transition.
        </motion.p>

        {/* Input frames */}
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <div className="relative rounded-xl overflow-hidden border border-white/[0.06]">
            <img src="/case-studies/stadium-vfx/input-first-frame.jpg" alt="First frame of green screen footage — two women taking a selfie against green screen in football jerseys" className="w-full aspect-video object-cover block" />
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent font-['Oswald'] text-[11px] tracking-[0.15em] uppercase text-white/45">
              Input — First Frame (Selfie)
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-white/[0.06]">
            <img src="/case-studies/stadium-vfx/input-last-frame.jpg" alt="Last frame of green screen footage — phone screen POV with blue and green screen behind" className="w-full aspect-video object-cover block" />
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent font-['Oswald'] text-[11px] tracking-[0.15em] uppercase text-white/45">
              Input — Last Frame (Phone POV)
            </div>
          </div>
        </motion.div>

        {/* Output frames */}
        <motion.h2 variants={fadeUp} className="font-['Oswald'] text-3xl md:text-5xl uppercase tracking-tight mt-16">The Result</motion.h2>
        <motion.p variants={fadeUp} className="text-sm font-extralight text-white/45 max-w-[640px] mt-4 leading-relaxed">
          Same frames after AI transformation — a packed stadium with cheering fans, celebrating
          players, and consistent environmental depth across the full camera pan.
        </motion.p>
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <div className="relative rounded-xl overflow-hidden border border-[#E50914]/15">
            <img src="/case-studies/stadium-vfx/output-first-frame.jpg" alt="AI-transformed first frame — women surrounded by cheering crowd in packed stadium" className="w-full aspect-video object-cover block" />
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent font-['Oswald'] text-[11px] tracking-[0.15em] uppercase text-[#E50914]">
              Output — First Frame
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-[#E50914]/15">
            <img src="/case-studies/stadium-vfx/output-last-frame.png" alt="AI-transformed last frame — phone POV showing packed stadium with celebrating players" className="w-full aspect-video object-cover block" />
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent font-['Oswald'] text-[11px] tracking-[0.15em] uppercase text-[#E50914]">
              Output — Last Frame
            </div>
          </div>
        </motion.div>
      </Section>

      <div className="h-px bg-white/[0.06] max-w-[1200px] mx-auto" />

      {/* ── Pipeline ───────────────────────────── */}
      <Section>
        <motion.p variants={fadeUp} className="text-[#E50914] text-[10px] tracking-[0.3em] uppercase mb-3">The Pipeline</motion.p>
        <motion.h2 variants={fadeUp} className="font-['Oswald'] text-3xl md:text-5xl uppercase tracking-tight">How We Solved It</motion.h2>
        <motion.p variants={fadeUp} className="text-sm font-extralight text-white/45 max-w-[640px] mt-4 leading-relaxed">
          A multi-stage AI pipeline combining image generation, video synthesis, frame interpolation, and broadcast-grade upscaling.
        </motion.p>

        <div className="relative mt-12">
          {/* Vertical line */}
          <div className="absolute left-[20px] top-0 bottom-0 w-px bg-gradient-to-b from-[#E50914] to-white/[0.06]" />

          {PIPELINE.map((step, i) => (
            <motion.div key={step.step} variants={fadeUp} className="flex gap-8 py-6 relative group">
              <div className="w-[41px] h-[41px] flex-shrink-0 border-2 border-white/[0.06] rounded-full flex items-center justify-center font-['Oswald'] text-xs text-white/20 bg-[#050505] z-[2] group-hover:border-[#E50914] group-hover:text-[#E50914] group-hover:shadow-[0_0_20px_rgba(229,9,20,0.2)] transition-all duration-400">
                {step.step}
              </div>
              <div className="flex-1">
                <div className="font-['Oswald'] text-base uppercase tracking-wide">{step.title}</div>
                <div className="text-[13px] font-extralight text-white/45 mt-2 leading-relaxed">{step.desc}</div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {step.tools.map(t => (
                    <span key={t} className="text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 rounded bg-[#E50914]/[0.08] text-[#E50914] border border-[#E50914]/[0.12]">{t}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      <div className="h-px bg-white/[0.06] max-w-[1200px] mx-auto" />

      {/* ── Upscale Script ─────────────────────── */}
      <Section>
        <motion.p variants={fadeUp} className="text-[#E50914] text-[10px] tracking-[0.3em] uppercase mb-3">Under the Hood</motion.p>
        <motion.h2 variants={fadeUp} className="font-['Oswald'] text-3xl md:text-5xl uppercase tracking-tight">The Upscale Pipeline</motion.h2>
        <motion.p variants={fadeUp} className="text-sm font-extralight text-white/45 max-w-[640px] mt-4 leading-relaxed">
          Our custom frame-by-frame upscaling script using RealESRGAN — processes each frame
          individually to avoid GPU memory overflow, with optional pre-denoising to reduce
          compression artifacts before upscaling.
        </motion.p>
        <motion.div variants={fadeUp} className="bg-[#0c0c0c] border border-white/[0.08] rounded-xl p-6 mt-6 overflow-x-auto relative">
          {/* Subtle glow behind code block */}
          <div className="absolute -inset-px bg-gradient-to-br from-[#E50914]/[0.06] via-transparent to-purple-500/[0.03] rounded-xl pointer-events-none" />
          <div className="relative">
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-white/[0.08]">
              {/* Window dots */}
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                </div>
                <span className="font-['Oswald'] text-[11px] uppercase tracking-[0.15em] text-white/50">upscale_frames.py</span>
              </div>
              <span className="text-[9px] tracking-[0.15em] uppercase text-[#E50914] px-2.5 py-1 bg-[#E50914]/[0.1] rounded border border-[#E50914]/[0.15]">Python · RealESRGAN</span>
            </div>
            <pre className="font-mono text-[13px] leading-[1.9] whitespace-pre overflow-x-auto">
<span style={{color:'#6a737d'}}>{"# Frame-by-frame video upscaler using RealESRGAN"}</span>{"\n"}
<span style={{color:'#6a737d'}}>{"# Processes one frame at a time to avoid OOM"}</span>{"\n"}
{"\n"}
<span style={{color:'#c792ea'}}>{"import"}</span><span style={{color:'#e4e4e4'}}>{" torch, glob, os, sys"}</span>{"\n"}
<span style={{color:'#c792ea'}}>{"from"}</span><span style={{color:'#e4e4e4'}}>{" PIL "}</span><span style={{color:'#c792ea'}}>{"import"}</span><span style={{color:'#e4e4e4'}}>{" Image, ImageFilter"}</span>{"\n"}
<span style={{color:'#c792ea'}}>{"from"}</span><span style={{color:'#e4e4e4'}}>{" spandrel "}</span><span style={{color:'#c792ea'}}>{"import"}</span><span style={{color:'#e4e4e4'}}>{" ModelLoader"}</span>{"\n"}
{"\n"}
<span style={{color:'#e4e4e4'}}>{"model "}</span><span style={{color:'#89ddff'}}>{"="}</span><span style={{color:'#82aaff'}}>{" ModelLoader"}</span><span style={{color:'#89ddff'}}>{"()."}</span><span style={{color:'#82aaff'}}>{"load_from_file"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"model_path"}</span><span style={{color:'#89ddff'}}>{")"}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"model "}</span><span style={{color:'#89ddff'}}>{"="}</span><span style={{color:'#e4e4e4'}}>{" model"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"cuda"}</span><span style={{color:'#89ddff'}}>{"()."}</span><span style={{color:'#82aaff'}}>{"half"}</span><span style={{color:'#89ddff'}}>{"()."}</span><span style={{color:'#82aaff'}}>{"eval"}</span><span style={{color:'#89ddff'}}>{"()"}</span>{"\n"}
{"\n"}
<span style={{color:'#c792ea'}}>{"for"}</span><span style={{color:'#e4e4e4'}}>{" i, f "}</span><span style={{color:'#c792ea'}}>{"in"}</span><span style={{color:'#82aaff'}}>{" enumerate"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"frames"}</span><span style={{color:'#89ddff'}}>{")"}</span><span style={{color:'#89ddff'}}>{":"}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"    img "}</span><span style={{color:'#89ddff'}}>{"="}</span><span style={{color:'#e4e4e4'}}>{" Image"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"open"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"f"}</span><span style={{color:'#89ddff'}}>{")"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"convert"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#c3e88d'}}>{'"RGB"'}</span><span style={{color:'#89ddff'}}>{")"}</span>{"\n"}
{"\n"}
<span style={{color:'#6a737d'}}>{"    # Optional: light denoise before upscaling"}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"    "}</span><span style={{color:'#c792ea'}}>{"if"}</span><span style={{color:'#e4e4e4'}}>{" denoise"}</span><span style={{color:'#89ddff'}}>{":"}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"        img "}</span><span style={{color:'#89ddff'}}>{"="}</span><span style={{color:'#e4e4e4'}}>{" img"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"filter"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"ImageFilter"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"GaussianBlur"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"radius"}</span><span style={{color:'#89ddff'}}>{"="}</span><span style={{color:'#f78c6c'}}>{"0.5"}</span><span style={{color:'#89ddff'}}>{"))"}</span>{"\n"}
{"\n"}
<span style={{color:'#e4e4e4'}}>{"    t "}</span><span style={{color:'#89ddff'}}>{"="}</span><span style={{color:'#e4e4e4'}}>{" torch"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"from_numpy"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"arr"}</span><span style={{color:'#89ddff'}}>{")"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"permute"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#f78c6c'}}>{"2"}</span><span style={{color:'#89ddff'}}>{","}</span><span style={{color:'#f78c6c'}}>{"0"}</span><span style={{color:'#89ddff'}}>{","}</span><span style={{color:'#f78c6c'}}>{"1"}</span><span style={{color:'#89ddff'}}>{")"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"half"}</span><span style={{color:'#89ddff'}}>{"()."}</span><span style={{color:'#82aaff'}}>{"cuda"}</span><span style={{color:'#89ddff'}}>{"()"}</span><span style={{color:'#e4e4e4'}}>{" / "}</span><span style={{color:'#f78c6c'}}>{"255.0"}</span>{"\n"}
{"\n"}
<span style={{color:'#e4e4e4'}}>{"    "}</span><span style={{color:'#c792ea'}}>{"with"}</span><span style={{color:'#e4e4e4'}}>{" torch"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"no_grad"}</span><span style={{color:'#89ddff'}}>{"()"}</span><span style={{color:'#89ddff'}}>{":"}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"        out "}</span><span style={{color:'#89ddff'}}>{"="}</span><span style={{color:'#82aaff'}}>{" model"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"t"}</span><span style={{color:'#89ddff'}}>{")"}</span>{"\n"}
{"\n"}
<span style={{color:'#e4e4e4'}}>{"    out_img "}</span><span style={{color:'#89ddff'}}>{"="}</span><span style={{color:'#e4e4e4'}}>{" Image"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"fromarray"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"out_arr"}</span><span style={{color:'#89ddff'}}>{")"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"resize"}</span><span style={{color:'#89ddff'}}>{"("}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"        ("}</span><span style={{color:'#e4e4e4'}}>{"target_w"}</span><span style={{color:'#89ddff'}}>{", "}</span><span style={{color:'#e4e4e4'}}>{"target_h"}</span><span style={{color:'#e4e4e4'}}>{")"}</span><span style={{color:'#89ddff'}}>{", "}</span><span style={{color:'#e4e4e4'}}>{"Image"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#ffcb6b'}}>{"LANCZOS"}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"    "}</span><span style={{color:'#89ddff'}}>{")"}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"    out_img"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"save"}</span><span style={{color:'#89ddff'}}>{"("}</span><span style={{color:'#e4e4e4'}}>{"out_path"}</span><span style={{color:'#89ddff'}}>{")"}</span>{"\n"}
<span style={{color:'#e4e4e4'}}>{"    torch"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#e4e4e4'}}>{"cuda"}</span><span style={{color:'#89ddff'}}>{"."}</span><span style={{color:'#82aaff'}}>{"empty_cache"}</span><span style={{color:'#89ddff'}}>{"()"}</span>
            </pre>
          </div>
        </motion.div>
      </Section>

      <div className="h-px bg-white/[0.06] max-w-[1200px] mx-auto" />

      {/* ── IP Ownership ───────────────────────── */}
      <Section>
        <motion.p variants={fadeUp} className="text-[#E50914] text-[10px] tracking-[0.3em] uppercase mb-3">Intellectual Property</motion.p>
        <motion.h2 variants={fadeUp} className="font-['Oswald'] text-3xl md:text-5xl uppercase tracking-tight">Why This Is<br/>Fully Client-Owned</motion.h2>
        <motion.p variants={fadeUp} className="text-sm font-extralight text-white/45 max-w-[640px] mt-4 leading-relaxed">
          Nothing in this pipeline was generated from scratch by AI. Every element traces back
          to human-authored source material — making all deliverables copyright-safe.
        </motion.p>
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {[
            ['Real Footage Base', 'Source material is client-owned green screen footage shot on a physical set with real actors.',
              'M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z'],
            ['Artist-Sketched References', 'Stadium layout, jerseys, crowd composition — all sketched by a human artist before AI generation.',
              'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 1 1 3.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z'],
            ['Copyright-Safe Under US Law', 'AI was used as a transformation tool on human-authored material — content retains copyright protection.',
              'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0 1 12 2.944a11.955 11.955 0 0 1-8.618 3.04A12.02 12.02 0 0 0 3 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'],
          ].map(([title, desc, icon]) => (
            <div key={title} className="bg-white/[0.03] border border-white/[0.06] rounded-xl backdrop-blur-xl p-8 text-center hover:border-white/[0.1] transition-all duration-400">
              <div className="w-12 h-12 mx-auto mb-4 border border-white/[0.06] rounded-full flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 stroke-white/45" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d={icon} />
                </svg>
              </div>
              <div className="font-['Oswald'] text-xs uppercase tracking-[0.1em] mb-2">{title}</div>
              <div className="text-xs font-extralight text-white/20 leading-relaxed">{desc}</div>
            </div>
          ))}
        </motion.div>
      </Section>

      <div className="h-px bg-white/[0.06] max-w-[1200px] mx-auto" />

      {/* ── CTA ────────────────────────────────── */}
      <Section className="text-center">
        <motion.p variants={fadeUp} className="text-[#E50914] text-[10px] tracking-[0.3em] uppercase mb-3">Ready to Transform Your Footage?</motion.p>
        <motion.h2 variants={fadeUp} className="font-['Oswald'] text-3xl md:text-5xl uppercase tracking-tight">
          Let's Build Your<br/>Next Production
        </motion.h2>
        <motion.p variants={fadeUp} className="text-sm font-extralight text-white/45 max-w-[480px] mx-auto mt-4 leading-relaxed">
          Whether you have green screen footage, raw camera work, or just a concept —
          we'll transform it into broadcast-ready content.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-8">
          <a href={CALENDLY} target="_blank" rel="noopener noreferrer"
            className="inline-block bg-[#E50914] text-white px-9 py-4 text-[11px] font-semibold tracking-[0.15em] uppercase no-underline hover:bg-[#B91C1C] transition-colors">
            Book a Call
          </a>
        </motion.div>
      </Section>

      {/* ── Footer ─────────────────────────────── */}
      <footer className="border-t border-white/[0.06] py-8 px-6 md:px-10 text-center text-[10px] text-white/20 tracking-[0.1em]">
        © 2026 MotionX Studios. All rights reserved. &nbsp;·&nbsp;
        <Link to="/" className="text-inherit no-underline hover:text-white/40 transition-colors">motionx.in</Link>
      </footer>
    </div>
  );
}
